import type { LoomAuthoringDocument } from "../features/parametric-geometry/loomAuthoringModel";
import { loomAuthoringDocumentStateId } from "../features/parametric-geometry/loomAuthoringHistory";
import {
  LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2,
  parseLoomJunctionFairingIntent,
  type LoomJunctionFairingIntentV2,
} from "../features/parametric-geometry/loomJunctionFairingIntent";
import {
  deriveLoomAircraftOccurrenceRebuildDefinition,
  parseLoomAuthoritativeRebuildRequestV4,
  type LoomAircraftOccurrenceRebuildDefinition,
} from "./loomRebuildV4";

export const LOOM_REBUILD_SCHEMA_V6 = "loom.authoritative-rebuild-request/v6" as const;
export const LOOM_REBUILD_DEFINITION_SCHEMA_V6 = "loom.aircraft-occurrence-rebuild-definition/v3" as const;
export const LOOM_REBUILD_EVALUATOR_V6 = "loom.native-aircraft-occurrence-transition-union/v2" as const;
export const LOOM_REBUILD_TOPOLOGY_V6 = "ordered-aircraft-occurrence-transition-continuity-union" as const;
export const LOOM_TRANSITION_SCHEMA_V3 = "loom.aircraft-junction-transition-definition/v3" as const;

export interface LoomAircraftJunctionTransitionV3 {
  schema: typeof LOOM_TRANSITION_SCHEMA_V3;
  transitionId: string;
  unionGroupId: string;
  parentA: { occurrenceId: string; patchId: string };
  parentB: { occurrenceId: string; patchId: string };
  theoreticalSeamId: string;
  construction: LoomJunctionFairingIntentV2["construction"];
}

export interface LoomAircraftOccurrenceTransitionRebuildDefinitionV3 {
  schema: typeof LOOM_REBUILD_DEFINITION_SCHEMA_V6;
  sourceDefinitions: LoomAircraftOccurrenceRebuildDefinition["sourceDefinitions"];
  occurrences: LoomAircraftOccurrenceRebuildDefinition["occurrences"];
  unionGroups: LoomAircraftOccurrenceRebuildDefinition["unionGroups"];
  transitions: LoomAircraftJunctionTransitionV3[];
}

export interface LoomAuthoritativeRebuildRequestV6 {
  schema: typeof LOOM_REBUILD_SCHEMA_V6;
  requestId: string;
  sourceDefinition: { documentId: string; revision: number; stateId: string };
  evaluatorId: typeof LOOM_REBUILD_EVALUATOR_V6;
  topology: typeof LOOM_REBUILD_TOPOLOGY_V6;
  definition: LoomAircraftOccurrenceTransitionRebuildDefinitionV3;
}

type Row = Record<string, unknown>;
const TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,159}$/;
function object(value: unknown, path: string): Row {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as Row;
}
function exact(row: Row, keys: readonly string[], path: string): void {
  if (Object.keys(row).length !== keys.length || Object.keys(row).some((key) => !keys.includes(key))) {
    throw new Error(`${path} has an unsupported field set`);
  }
}
function token(value: unknown, path: string): string {
  if (typeof value !== "string" || !TOKEN.test(value)) throw new Error(`${path} is invalid`);
  return value;
}
function parseParent(value: unknown, path: string) {
  const row = object(value, path); exact(row, ["occurrenceId", "patchId"], path);
  return { occurrenceId: token(row.occurrenceId, `${path}.occurrenceId`), patchId: token(row.patchId, `${path}.patchId`) };
}
function parseTransition(value: unknown, index: number): LoomAircraftJunctionTransitionV3 {
  const path = `definition.transitions[${index}]`; const row = object(value, path);
  exact(row, ["schema", "transitionId", "unionGroupId", "parentA", "parentB", "theoreticalSeamId", "construction"], path);
  if (row.schema !== LOOM_TRANSITION_SCHEMA_V3) throw new Error(`${path}.schema is unsupported`);
  const intent = parseLoomJunctionFairingIntent({
    schema: LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2,
    transitionId: row.transitionId,
    construction: row.construction,
  });
  if (intent.schema !== LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2) throw new Error(`${path}.construction is unsupported`);
  return { schema: LOOM_TRANSITION_SCHEMA_V3, transitionId: token(row.transitionId, `${path}.transitionId`),
    unionGroupId: token(row.unionGroupId, `${path}.unionGroupId`), parentA: parseParent(row.parentA, `${path}.parentA`),
    parentB: parseParent(row.parentB, `${path}.parentB`), theoreticalSeamId: token(row.theoreticalSeamId, `${path}.theoreticalSeamId`),
    construction: intent.construction };
}

export function deriveLoomAircraftOccurrenceTransitionRebuildDefinitionV3(
  document: LoomAuthoringDocument,
): LoomAircraftOccurrenceTransitionRebuildDefinitionV3 | null {
  const base = deriveLoomAircraftOccurrenceRebuildDefinition(document);
  if (!base || base.unionGroups.length !== 1) return null;
  const sourceWingId = base.sourceDefinitions[0]?.definition.wing.sourceNodeId;
  const sourceWing = document.nodes.find((node) => node.id === sourceWingId && node.type === "primitive"
    && node.definition.primitive === "wing");
  if (!sourceWing || sourceWing.type !== "primitive" || sourceWing.definition.primitive !== "wing"
      || !sourceWing.definition.junctionFairing) return null;
  const intent = parseLoomJunctionFairingIntent(sourceWing.definition.junctionFairing);
  if (intent.schema !== LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2) return null;
  const group = base.unionGroups[0]!;
  const wings = base.occurrences.filter((row) => row.role === "wing" && row.parity === "proper");
  const fuselages = base.occurrences.filter((row) => row.role === "fuselage");
  if (wings.length !== 1 || fuselages.length !== 1) return null;
  const parentA = wings[0]!; const parentB = fuselages[0]!;
  return { schema: LOOM_REBUILD_DEFINITION_SCHEMA_V6, sourceDefinitions: base.sourceDefinitions,
    occurrences: base.occurrences, unionGroups: base.unionGroups, transitions: [{
      schema: LOOM_TRANSITION_SCHEMA_V3, transitionId: intent.transitionId, unionGroupId: group.unionGroupId,
      parentA: { occurrenceId: parentA.occurrenceId, patchId: `${parentA.occurrenceId}::${group.unionGroupId}:wing-trimmed` },
      parentB: { occurrenceId: parentB.occurrenceId, patchId: `${parentB.occurrenceId}::${group.unionGroupId}:fuselage-trimmed` },
      theoreticalSeamId: `${group.unionGroupId}:${parentA.occurrenceId}:${parentB.occurrenceId}`,
      construction: intent.construction,
    }] };
}

export function deriveLoomAuthoritativeRebuildRequestV6(
  document: LoomAuthoringDocument, requestId: string,
): LoomAuthoritativeRebuildRequestV6 | null {
  const definition = deriveLoomAircraftOccurrenceTransitionRebuildDefinitionV3(document);
  if (!definition) return null;
  return parseLoomAuthoritativeRebuildRequestV6({ schema: LOOM_REBUILD_SCHEMA_V6, requestId,
    sourceDefinition: { documentId: document.documentId, revision: document.revision, stateId: loomAuthoringDocumentStateId(document) },
    evaluatorId: LOOM_REBUILD_EVALUATOR_V6, topology: LOOM_REBUILD_TOPOLOGY_V6, definition });
}

export function parseLoomAuthoritativeRebuildRequestV6(value: unknown): LoomAuthoritativeRebuildRequestV6 {
  const root = object(value, "v6 request"); exact(root, ["schema", "requestId", "sourceDefinition", "evaluatorId", "topology", "definition"], "v6 request");
  if (root.schema !== LOOM_REBUILD_SCHEMA_V6 || root.evaluatorId !== LOOM_REBUILD_EVALUATOR_V6 || root.topology !== LOOM_REBUILD_TOPOLOGY_V6) {
    throw new Error("v6 request markers are unsupported");
  }
  const definition = object(root.definition, "v6 definition");
  exact(definition, ["schema", "sourceDefinitions", "occurrences", "unionGroups", "transitions"], "v6 definition");
  if (definition.schema !== LOOM_REBUILD_DEFINITION_SCHEMA_V6 || !Array.isArray(definition.transitions)
      || definition.transitions.length < 1 || definition.transitions.length > 4) throw new Error("v6 transition table is invalid");
  const base = parseLoomAuthoritativeRebuildRequestV4({ schema: "loom.authoritative-rebuild-request/v4", requestId: root.requestId,
    sourceDefinition: root.sourceDefinition, evaluatorId: "loom.native-aircraft-occurrence-union/v1", topology: "ordered-aircraft-occurrence-union",
    definition: { schema: "loom.aircraft-occurrence-rebuild-definition/v1", sourceDefinitions: definition.sourceDefinitions,
      occurrences: definition.occurrences, unionGroups: definition.unionGroups } });
  const transitions = definition.transitions.map(parseTransition).sort((a, b) => a.transitionId.localeCompare(b.transitionId));
  if (new Set(transitions.map((row) => row.transitionId)).size !== transitions.length) throw new Error("v6 transition IDs must be unique");
  const occurrences = new Map(base.definition.occurrences.map((row) => [row.occurrenceId, row]));
  const groups = new Map(base.definition.unionGroups.map((row) => [row.unionGroupId, row]));
  transitions.forEach((transition) => {
    const group = groups.get(transition.unionGroupId); const parents = [transition.parentA, transition.parentB];
    if (!group || parents.some((parent) => !group.orderedOccurrenceIds.includes(parent.occurrenceId))
        || occurrences.get(transition.parentA.occurrenceId)?.role !== "wing"
        || occurrences.get(transition.parentB.occurrenceId)?.role !== "fuselage") throw new Error("v6 transition source-surface chart is unavailable");
    parents.forEach((parent) => {
      const role = occurrences.get(parent.occurrenceId)!.role;
      if (parent.patchId !== `${parent.occurrenceId}::${group.unionGroupId}:${role}-trimmed`) throw new Error("v6 transition parent patch authority is not exact");
    });
    if (transition.theoreticalSeamId !== `${group.unionGroupId}:${transition.parentA.occurrenceId}:${transition.parentB.occurrenceId}`) {
      throw new Error("v6 transition theoretical seam authority is not exact");
    }
  });
  const result: LoomAuthoritativeRebuildRequestV6 = { schema: LOOM_REBUILD_SCHEMA_V6, requestId: base.requestId, sourceDefinition: base.sourceDefinition,
    evaluatorId: LOOM_REBUILD_EVALUATOR_V6, topology: LOOM_REBUILD_TOPOLOGY_V6,
    definition: { schema: LOOM_REBUILD_DEFINITION_SCHEMA_V6, sourceDefinitions: base.definition.sourceDefinitions,
      occurrences: base.definition.occurrences, unionGroups: base.definition.unionGroups, transitions } };
  if (new TextEncoder().encode(JSON.stringify(result)).byteLength > 1_052_672) throw new Error("v6 request exceeds its serialized budget");
  return result;
}
